import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, X, Save, Trash2, Loader2, Upload, Star, Palette,
  Edit2, CheckCircle, Search, AlertCircle, Film,
} from 'lucide-react';
import type { MediaBrandProfile, MediaBrandProfileInput } from './types';

const DEFAULT_PROFILE: MediaBrandProfileInput = {
  name: '',
  agency_name: null,
  primary_color: '#7c3aed',
  secondary_color: '#ec4899',
  logo_url: null,
  logo_position: 'top-left',
  logo_size: 'md',
  background_color: null,
  theme: 'dark',
  cta_label: null,
  cta_url: null,
  show_social_share: true,
  logo_intro_enabled: false,
  logo_outro_enabled: false,
  logo_bumper_duration: 3,
  logo_bumper_tagline: null,
  is_default: false,
};

const BUMPER_DURATIONS = [
  { value: 2, label: '2s' },
  { value: 3, label: '3s' },
  { value: 4, label: '4s' },
  { value: 5, label: '5s' },
];

const LOGO_SIZES: { value: 'sm' | 'md' | 'lg' | 'xl'; label: string }[] = [
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'X-Large' },
];

// Must mirror the header logo heights used by the Screen Record share page.
const LOGO_PREVIEW_HEIGHTS: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-11',
  xl: 'h-14',
};

// ============================================================
// BRAND PROFILE EDITOR
// ============================================================

interface BrandEditorProps {
  profile: Partial<MediaBrandProfile> | null;
  onSave: (input: MediaBrandProfileInput) => Promise<void>;
  onUploadLogo: (file: File) => Promise<string>;
  onCancel: () => void;
  saving: boolean;
  accentColor?: string;
  /** 'full' shows all fields; 'watermark' hides video-specific sections (CTA, bumpers, social share) */
  mode?: 'full' | 'watermark';
}

function BrandProfileEditor({ profile, onSave, onUploadLogo, onCancel, saving, accentColor = '#7c3aed', mode = 'full' }: BrandEditorProps) {
  const [form, setForm] = useState<MediaBrandProfileInput>(() => {
    if (!profile) return DEFAULT_PROFILE;
    return {
      name: profile.name || '',
      agency_name: profile.agency_name ?? null,
      primary_color: profile.primary_color || '#7c3aed',
      secondary_color: profile.secondary_color || '#ec4899',
      logo_url: profile.logo_url ?? null,
      logo_position: profile.logo_position || 'top-left',
      logo_size: profile.logo_size || 'md',
      background_color: profile.background_color ?? null,
      theme: profile.theme || 'dark',
      cta_label: profile.cta_label ?? null,
      cta_url: profile.cta_url ?? null,
      show_social_share: profile.show_social_share ?? true,
      logo_intro_enabled: profile.logo_intro_enabled ?? false,
      logo_outro_enabled: profile.logo_outro_enabled ?? false,
      logo_bumper_duration: profile.logo_bumper_duration ?? 3,
      logo_bumper_tagline: profile.logo_bumper_tagline ?? null,
      is_default: profile.is_default ?? false,
    };
  });

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof MediaBrandProfileInput>(key: K, value: MediaBrandProfileInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Logo must be under 5 MB'); return; }
    setUploading(true);
    setError(null);
    try {
      const url = await onUploadLogo(file);
      update('logo_url', url);
    } catch (err: any) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Brand name is required'); return; }
    setError(null);
    try {
      await onSave({ ...form, name: form.name.trim() });
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    }
  };

  const gradientPreview = { background: `linear-gradient(to right, ${form.primary_color}, ${form.secondary_color})` };
  const previewDark = (form.theme || 'dark') !== 'light';
  const previewBg = form.background_color || (previewDark ? '#020617' : '#f8fafc');

  return (
    <div className="space-y-5">
      {/* Preview bar */}
      <div className="h-1.5 rounded-full" style={gradientPreview} />

      {/* Brand Name */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Brand / Client Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={e => update('name', e.target.value)}
          placeholder="e.g. Acme Real Estate"
          className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-purple-500 transition-colors placeholder-slate-600"
        />
      </div>

      {/* Agency Name */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Agency / Company Name</label>
        <input
          type="text"
          value={form.agency_name || ''}
          onChange={e => update('agency_name', e.target.value || null)}
          placeholder="Shown in the viewer header"
          className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-purple-500 transition-colors placeholder-slate-600"
        />
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Primary Color</label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.primary_color} onChange={e => update('primary_color', e.target.value)} className="w-8 h-8 rounded border border-slate-700 cursor-pointer bg-transparent" />
            <input type="text" value={form.primary_color} onChange={e => update('primary_color', e.target.value)} className="flex-1 bg-slate-800 border border-slate-700 text-white text-xs font-mono rounded-lg px-3 py-2 outline-none focus:border-purple-500" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">Secondary Color</label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.secondary_color} onChange={e => update('secondary_color', e.target.value)} className="w-8 h-8 rounded border border-slate-700 cursor-pointer bg-transparent" />
            <input type="text" value={form.secondary_color} onChange={e => update('secondary_color', e.target.value)} className="flex-1 bg-slate-800 border border-slate-700 text-white text-xs font-mono rounded-lg px-3 py-2 outline-none focus:border-purple-500" />
          </div>
        </div>
      </div>

      {/* Logo */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Logo</label>
        {form.logo_url ? (
          <div className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-lg p-3">
            <img src={form.logo_url} alt="Logo" className="h-8 object-contain" />
            <div className="flex-1" />
            <button type="button" onClick={() => update('logo_url', null)} className="p-1.5 text-slate-400 hover:text-red-400 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 border border-dashed border-slate-600 rounded-lg text-sm text-slate-400 hover:border-slate-500 hover:text-slate-300 transition-colors cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload logo'}
            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploading} />
          </label>
        )}
      </div>

      {/* Logo Position */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Logo Position</label>
        <div className="flex gap-2">
          {(['top-left', 'top-right'] as const).map(pos => (
            <button
              key={pos}
              type="button"
              onClick={() => update('logo_position', pos)}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                form.logo_position === pos
                  ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                  : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
              }`}
            >
              {pos === 'top-left' ? 'Top Left' : 'Top Right'}
            </button>
          ))}
        </div>
      </div>

      {/* Logo Size */}
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1.5">Logo Size</label>
        <p className="text-[11px] text-slate-600 mb-2 -mt-0.5">Controls the logo in the share-page header.</p>
        <div className="flex gap-2">
          {LOGO_SIZES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => update('logo_size', value)}
              className={`flex-1 px-2 py-2 text-xs font-medium rounded-lg border transition-colors ${
                (form.logo_size || 'md') === value
                  ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                  : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Live share-page header preview (reflects logo size, theme + background) */}
        <div className="mt-2.5 rounded-xl border border-slate-700 overflow-hidden">
          <div
            className="px-3 py-2.5 flex items-center gap-2.5 min-h-[3.75rem]"
            style={{ background: previewBg }}
          >
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt=""
                className={`${LOGO_PREVIEW_HEIGHTS[form.logo_size || 'md']} max-w-[9rem] object-contain`}
              />
            ) : (
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white shrink-0"
                style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})` }}
              >
                {(form.agency_name || form.name || 'B').slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className={`text-sm font-semibold truncate ${previewDark ? 'text-white' : 'text-slate-900'}`}>
              {form.agency_name || form.name || 'Your brand'}
            </span>
            <span className="flex-1" />
            <span
              className={`text-[10px] font-medium px-2 py-1 rounded-md border shrink-0 ${
                previewDark ? 'text-slate-200 border-slate-700 bg-slate-800/80' : 'text-slate-700 border-slate-200 bg-white'
              }`}
            >
              Share
            </span>
          </div>
        </div>
        <p className="text-[11px] text-slate-600 mt-1">
          Live preview of the share-page header{form.logo_url ? '' : ' — upload a logo above to preview its size'}.
        </p>
      </div>

      {/* Share-page appearance: theme + background colour */}
      <div className="space-y-3 pt-1">
        <label className="block text-xs font-medium text-slate-400">Share Page Colours</label>
        <div className="flex gap-2">
          {(['dark', 'light'] as const).map(th => (
            <button
              key={th}
              type="button"
              onClick={() => update('theme', th)}
              className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                (form.theme || 'dark') === th
                  ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                  : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'
              }`}
            >
              {th === 'dark' ? 'Dark' : 'Light'}
            </button>
          ))}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={form.background_color || (form.theme === 'light' ? '#f8fafc' : '#020617')}
              onChange={e => update('background_color', e.target.value)}
              className="w-8 h-8 rounded border border-slate-700 cursor-pointer bg-transparent"
              aria-label="Custom background colour"
            />
            <input
              type="text"
              value={form.background_color || ''}
              onChange={e => update('background_color', e.target.value || null)}
              placeholder="Auto (theme default)"
              className="flex-1 bg-slate-800 border border-slate-700 text-white text-xs font-mono rounded-lg px-3 py-2 outline-none focus:border-purple-500 placeholder-slate-600"
            />
            {form.background_color && (
              <button
                type="button"
                onClick={() => update('background_color', null)}
                className="p-2 text-slate-500 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                aria-label="Reset background to theme default"
                title="Reset to theme default"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-600 mt-1">Optional custom page background. Leave blank to use the theme default.</p>
        </div>
      </div>

      {mode === 'full' && (
        <>
          {/* CTA */}
          <div className="space-y-3">
            <label className="block text-xs font-medium text-slate-400">Call to Action Button</label>
            <input
              type="text"
              value={form.cta_label || ''}
              onChange={e => update('cta_label', e.target.value || null)}
              placeholder="Button text (e.g. View Portal)"
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-purple-500 transition-colors placeholder-slate-600"
            />
            <input
              type="url"
              value={form.cta_url || ''}
              onChange={e => update('cta_url', e.target.value || null)}
              placeholder="https://your-portal.com"
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-purple-500 transition-colors placeholder-slate-600"
            />
          </div>

          {/* Social Share Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-xs font-medium text-slate-400">Social Share Buttons</label>
              <p className="text-[11px] text-slate-600 mt-0.5">Show share options on the branded viewer</p>
            </div>
            <button
              type="button"
              onClick={() => update('show_social_share', !form.show_social_share)}
              className={`relative w-10 h-5.5 rounded-full transition-colors ${form.show_social_share ? 'bg-purple-600' : 'bg-slate-700'}`}
            >
              <div className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${form.show_social_share ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Logo Intro/Outro Bumpers */}
          <div className="space-y-3 pt-2 border-t border-slate-700/50">
            <div className="flex items-center gap-2">
              <Film className="w-4 h-4 text-slate-400" />
              <label className="text-xs font-medium text-slate-400">Video Logo Bumpers</label>
            </div>
            <p className="text-[11px] text-slate-600 -mt-1">
              Default intro/outro logo cards applied to new projects using this brand
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Logo intro card (start of video)</span>
                <button
                  type="button"
                  onClick={() => update('logo_intro_enabled', !form.logo_intro_enabled)}
                  className={`relative w-10 h-5.5 rounded-full transition-colors ${form.logo_intro_enabled ? 'bg-purple-600' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${form.logo_intro_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Logo outro card (end of video)</span>
                <button
                  type="button"
                  onClick={() => update('logo_outro_enabled', !form.logo_outro_enabled)}
                  className={`relative w-10 h-5.5 rounded-full transition-colors ${form.logo_outro_enabled ? 'bg-purple-600' : 'bg-slate-700'}`}
                >
                  <div className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${form.logo_outro_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            {(form.logo_intro_enabled || form.logo_outro_enabled) && (
              <div className="space-y-3 pt-2">
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Bumper Duration</label>
                  <div className="flex gap-2">
                    {BUMPER_DURATIONS.map(d => (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => update('logo_bumper_duration', d.value)}
                        className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          (form.logo_bumper_duration || 3) === d.value
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Tagline (optional)</label>
                  <input
                    type="text"
                    value={form.logo_bumper_tagline || ''}
                    onChange={e => update('logo_bumper_tagline', e.target.value || null)}
                    placeholder="e.g. Your Agency Name • yoursite.com"
                    maxLength={60}
                    className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-purple-500 transition-colors placeholder-slate-600"
                  />
                  <p className="text-[10px] text-slate-600 mt-1">Shown below the logo on bumper cards</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Default toggle */}
      <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
        <div>
          <p className="text-xs font-medium text-slate-300">Set as Default Brand</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Auto-applied to new projects</p>
        </div>
        <button
          type="button"
          onClick={() => update('is_default', !form.is_default)}
          className={`relative w-10 h-5.5 rounded-full transition-colors ${form.is_default ? 'bg-purple-600' : 'bg-slate-700'}`}
        >
          <div className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${form.is_default ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
          style={{ background: accentColor }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {profile?.id ? 'Save Changes' : 'Create Brand'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================
// MEDIA BRAND MANAGER (main component)
// ============================================================

export interface MediaBrandManagerProps {
  userId: string;
  brands: MediaBrandProfile[];
  loading: boolean;
  onClose: () => void;
  onLoadBrands: () => Promise<MediaBrandProfile[]>;
  onCreateBrand: (input: MediaBrandProfileInput) => Promise<MediaBrandProfile>;
  onUpdateBrand: (id: string, input: MediaBrandProfileInput) => Promise<void>;
  onDeleteBrand: (id: string) => Promise<void>;
  onUploadLogo: (file: File) => Promise<string>;
  onBrandApplied?: (brand: MediaBrandProfile) => void;
  applyMode?: boolean;
  accentColor?: string;
  /** 'full' shows all fields; 'watermark' hides video-specific sections (CTA, bumpers, social share) */
  mode?: 'full' | 'watermark';
}

export function MediaBrandManager({
  userId,
  brands,
  loading,
  onClose,
  onLoadBrands,
  onCreateBrand,
  onUpdateBrand,
  onDeleteBrand,
  onUploadLogo,
  onBrandApplied,
  applyMode = false,
  accentColor = '#7c3aed',
  mode = 'full',
}: MediaBrandManagerProps) {
  const [editingBrand, setEditingBrand] = useState<Partial<MediaBrandProfile> | null | 'new'>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!editingBrand) setSearch('');
  }, [editingBrand]);

  const handleSave = async (input: MediaBrandProfileInput) => {
    setSaving(true);
    try {
      if (editingBrand && editingBrand !== 'new' && editingBrand.id) {
        await onUpdateBrand(editingBrand.id, input);
      } else {
        await onCreateBrand(input);
      }
      await onLoadBrands();
      setEditingBrand(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this brand profile? Projects using it will fall back to your account-level brand settings.')) return;
    setDeleting(id);
    try {
      await onDeleteBrand(id);
      await onLoadBrands();
    } finally {
      setDeleting(null);
    }
  };

  const filteredBrands = brands.filter(
    b => !search.trim() || b.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}80)` }}
            >
              <Palette size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-100">
                {editingBrand
                  ? editingBrand === 'new' ? 'New Brand Profile' : 'Edit Brand Profile'
                  : 'Brand Profiles'}
              </h2>
              <p className="text-xs text-slate-400">
                {editingBrand
                  ? 'Configure logo, colors, and viewer branding'
                  : 'Save and reuse client branding across projects'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {editingBrand ? (
            <BrandProfileEditor
              profile={editingBrand === 'new' ? null : editingBrand}
              onSave={handleSave}
              onUploadLogo={onUploadLogo}
              onCancel={() => setEditingBrand(null)}
              saving={saving}
              accentColor={accentColor}
              mode={mode}
            />
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
            </div>
          ) : brands.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette size={28} className="text-slate-500" />
              </div>
              <h3 className="text-base font-semibold text-slate-300 mb-2">No brand profiles yet</h3>
              <p className="text-sm text-slate-500 mb-6">
                Create a brand profile to save your client's logo, colours, and viewer styling.
              </p>
              <button
                onClick={() => setEditingBrand('new')}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors"
                style={{ background: accentColor }}
              >
                <Plus size={16} />
                Create First Brand
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search brands..."
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                />
              </div>

              {filteredBrands.map(brand => (
                <div
                  key={brand.id}
                  className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-slate-600 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    {/* Logo preview */}
                    <div className="w-12 h-12 rounded-xl border border-slate-600 overflow-hidden bg-white flex items-center justify-center shrink-0">
                      {brand.logo_url ? (
                        <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-contain p-1" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-500">
                          <Palette size={20} />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-slate-100 truncate">{brand.name}</h4>
                        {brand.is_default && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-900/30 text-purple-400 rounded-full text-[10px] font-medium">
                            <Star size={10} /> Default
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        {brand.agency_name && (
                          <span className="text-[11px] text-slate-500 truncate max-w-[140px]">{brand.agency_name}</span>
                        )}
                        <div className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 rounded-full border border-slate-600" style={{ background: brand.primary_color }} />
                          <div className="w-3.5 h-3.5 rounded-full border border-slate-600" style={{ background: brand.secondary_color }} />
                        </div>
                        <div className="h-1 w-12 rounded-full" style={{ background: `linear-gradient(to right, ${brand.primary_color}, ${brand.secondary_color})` }} />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex items-center gap-1.5 transition-opacity ${applyMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                      {applyMode && onBrandApplied && (
                        <button
                          onClick={() => onBrandApplied(brand)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-semibold transition-colors"
                          style={{ background: accentColor }}
                        >
                          <CheckCircle size={12} />
                          Apply
                        </button>
                      )}
                      <button
                        onClick={() => setEditingBrand(brand)}
                        className="p-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(brand.id)}
                        disabled={deleting === brand.id}
                        className="p-2 bg-red-900/20 hover:bg-red-900/30 text-red-400 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {deleting === brand.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — Create New */}
        {!editingBrand && !loading && brands.length > 0 && (
          <div className="p-4 border-t border-slate-800">
            <button
              onClick={() => setEditingBrand('new')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-colors border border-slate-700 border-dashed"
            >
              <Plus size={16} />
              Add Brand Profile
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
